import * as React from 'react';
import { useTranslation } from 'react-i18next';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { describeAuthError } from '@/lib/authErrors';
import { usePartnersAuth } from '@partners/auth/PartnersAuth';
import { Notice, Section, Shell } from '@partners/components/Shell';
import { claim, passwordSet, promoterErrorKey, whoami, type WhoAmI } from '@partners/data/promoter';
import { Agreement } from './Agreement';
import { Home } from './Home';

const PASSWORD_SKIP_KEY = 'coreway.partners.passwordSkipped';

function passwordSkipped(userId: string): boolean {
  try {
    return localStorage.getItem(PASSWORD_SKIP_KEY) === userId;
  } catch {
    return false;
  }
}

function rememberSkip(userId: string): void {
  try {
    localStorage.setItem(PASSWORD_SKIP_KEY, userId);
  } catch {
    // Storage blocked: the screen asks again next time, which is harmless.
  }
}

/**
 * The signed-in gate, in the order of design §9.3: program closed > not invited > claim > set password (once,
 * skippable) > agreement (blocks everything) > home. Every step re-reads promoter_whoami, the one source of
 * the state; nothing is inferred on the client.
 */
export function Gate() {
  const { t } = useTranslation();
  const { client, session, signOut } = usePartnersAuth();
  const [who, setWho] = React.useState<WhoAmI | null>(null);
  const [error, setError] = React.useState<string | null>(null);
  const [skipped, setSkipped] = React.useState(() => (session ? passwordSkipped(session.user.id) : false));

  const refresh = React.useCallback(async () => {
    setError(null);
    try {
      setWho(await whoami(client));
    } catch (err) {
      console.warn('promoter_whoami failed', err);
      setError(t(`promoter.errors.${promoterErrorKey(err)}`));
    }
  }, [client, t]);

  React.useEffect(() => {
    void refresh();
  }, [refresh]);

  const out = () => void signOut();
  const email = session?.user.email ?? '';

  if (error) {
    return (
      <Shell onSignOut={out}>
        <Notice>{error}</Notice>
        <Button variant="outline" onClick={() => void refresh()}>
          {t('promoter.common.retry')}
        </Button>
      </Shell>
    );
  }
  if (!who) {
    return (
      <Shell onSignOut={out}>
        <p className="text-ink-muted" role="status">
          {t('promoter.common.loading')}
        </p>
      </Shell>
    );
  }
  if (!who.enabled) {
    return (
      <Shell onSignOut={out}>
        <Message title={t('promoter.gate.closed.title')} body={t('promoter.gate.closed.body')} />
      </Shell>
    );
  }
  if (who.state === 'not_invited') {
    return (
      <Shell onSignOut={out}>
        <Message title={t('promoter.gate.notInvited.title')} body={t('promoter.gate.notInvited.body', { email })}>
          <Button onClick={out}>{t('promoter.common.signOut')}</Button>
        </Message>
      </Shell>
    );
  }
  if (who.state === 'claimable') {
    return (
      <Shell onSignOut={out}>
        <Claim email={email} onClaimed={setWho} />
      </Shell>
    );
  }
  if (!who.has_password && !skipped) {
    return (
      <Shell onSignOut={out}>
        <SetPassword
          onDone={(didSkip) => {
            if (didSkip && session) rememberSkip(session.user.id);
            setSkipped(true);
          }}
        />
      </Shell>
    );
  }
  if (who.terms_pending) {
    return (
      <Shell onSignOut={out} wide>
        <Agreement onSigned={() => void refresh()} />
      </Shell>
    );
  }
  return (
    <Shell onSignOut={out} wide>
      <Home onGateChanged={() => void refresh()} />
    </Shell>
  );
}

function Message({ title, body, children }: { title: string; body: string; children?: React.ReactNode }) {
  return (
    <div className="flex flex-col gap-4 rounded-lg border border-hairline bg-card p-6 shadow-card">
      <h1 className="text-xl font-semibold text-ink">{title}</h1>
      <p className="text-base text-ink-secondary">{body}</p>
      {children}
    </div>
  );
}

function Claim({ email, onClaimed }: { email: string; onClaimed: (who: WhoAmI) => void }) {
  const { t } = useTranslation();
  const { client } = usePartnersAuth();
  const [busy, setBusy] = React.useState(false);
  const [error, setError] = React.useState<string | null>(null);

  async function onClaim() {
    setBusy(true);
    setError(null);
    try {
      onClaimed(await claim(client));
    } catch (err) {
      console.warn('promoter_claim refused', err);
      setError(t(`promoter.errors.${promoterErrorKey(err)}`));
      setBusy(false);
    }
  }

  return (
    <Message title={t('promoter.gate.claim.title')} body={t('promoter.gate.claim.body', { email })}>
      <Notice>{error}</Notice>
      <Button onClick={() => void onClaim()} disabled={busy}>
        {busy ? t('promoter.gate.claim.busy') : t('promoter.gate.claim.button')}
      </Button>
    </Message>
  );
}

function SetPassword({ onDone }: { onDone: (skipped: boolean) => void }) {
  const { t } = useTranslation();
  const { client } = usePartnersAuth();
  const [password, setPassword] = React.useState('');
  const [confirm, setConfirm] = React.useState('');
  const [busy, setBusy] = React.useState(false);
  const [error, setError] = React.useState<string | null>(null);

  async function onSave(event: React.FormEvent) {
    event.preventDefault();
    if (password.length < 8) return setError(t('promoter.gate.setPassword.short'));
    if (password !== confirm) return setError(t('promoter.gate.setPassword.mismatch'));
    setBusy(true);
    setError(null);
    const { error: err } = await client.auth.updateUser({ password });
    if (err) {
      setBusy(false);
      console.warn('password not set', err);
      return setError(t(`promoter.auth.errors.${describeAuthError(err) ?? 'generic'}`));
    }
    // GoTrue cannot tell us later (an emailed-code account carries a random hash): record it (0276). The
    // password IS set; a failed record only means the step may be offered again, so it never blocks.
    try {
      await passwordSet(client);
    } catch (recordErr) {
      console.warn('promoter_password_set failed', recordErr);
    }
    setBusy(false);
    onDone(false);
  }

  return (
    <Section id="set-password" title={t('promoter.gate.setPassword.title')}>
      <p className="mb-4 text-base text-ink-secondary">{t('promoter.gate.setPassword.body')}</p>
      <form className="flex flex-col gap-4" onSubmit={(e) => void onSave(e)}>
        <Notice>{error}</Notice>
        <div className="flex flex-col gap-1.5">
          <Label htmlFor="new-password">{t('promoter.gate.setPassword.password')}</Label>
          <Input id="new-password" type="password" autoComplete="new-password" value={password} onChange={(e) => setPassword(e.target.value)} />
        </div>
        <div className="flex flex-col gap-1.5">
          <Label htmlFor="confirm-password">{t('promoter.gate.setPassword.confirm')}</Label>
          <Input id="confirm-password" type="password" autoComplete="new-password" value={confirm} onChange={(e) => setConfirm(e.target.value)} />
        </div>
        <div className="flex flex-col-reverse gap-2 sm:flex-row sm:justify-end">
          <Button type="button" variant="ghost" onClick={() => onDone(true)}>
            {t('promoter.gate.setPassword.skip')}
          </Button>
          <Button type="submit" disabled={busy}>
            {busy ? t('promoter.gate.setPassword.saving') : t('promoter.gate.setPassword.save')}
          </Button>
        </div>
      </form>
    </Section>
  );
}
